import React, { useState, useEffect } from 'react';
import { X, Users, MapPin, Briefcase, Award, Star } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getDefaultAvatar } from '../utils/avatars';
import FollowButton from './FollowButton';
import FriendRequestButton from './FriendRequestButton';

export default function UserProfilePopup({ userId, currentUserId, onClose }) {
  const [userData, setUserData] = useState(null);
  const [followCounts, setFollowCounts] = useState({ followers: 0, following: 0 });
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    if (userId) {
      loadUserData();
      loadFollowCounts();
    }
  }, [userId]);

  const loadUserData = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));

      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData({
          displayName: data.displayName || 'User',
          bio: data.bio || '',
          location: data.location || '',
          traderType: data.traderType || '',
          tradingExperience: data.tradingExperience || '',
          favoriteStocks: data.favoriteStocks || '',
          avatar: data.avatar || getDefaultAvatar()
        });
      } else {
        setUserData({
          displayName: 'User',
          avatar: getDefaultAvatar()
        });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      setUserData({
        displayName: 'User',
        avatar: getDefaultAvatar()
      });
    } finally {
      setLoading(false);
    }
  };

  const loadFollowCounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/follow-counts`);
      const data = await response.json();

      if (data.success) {
        setFollowCounts({
          followers: data.followers,
          following: data.following
        });
      }
    } catch (error) {
      console.error('Error loading follow counts:', error);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-80 mx-4 overflow-hidden relative" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition z-10 bg-white rounded-full p-1"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Top Section with gradient and avatar */}
        <div className="h-28 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center px-6 gap-4">
          <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center text-4xl shadow-lg flex-shrink-0">
            {userData?.avatar?.startsWith('data:image') ? (
              <img
                src={userData.avatar}
                alt="Avatar"
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <span>{userData?.avatar || '😊'}</span>
            )}
          </div>
          {/* Username - Right of avatar */}
          <h2 className="text-xl font-bold text-white">{userData?.displayName || 'User'}</h2>
        </div>

        {/* User details and stats - Two columns */}
        <div className="bg-white px-6 py-4 flex gap-6">
          {/* Left column - User details */}
          <div className="flex-1 space-y-3">
            {userData?.location && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <MapPin className="w-4 h-4 text-purple-500" />
                <span>{userData.location}</span>
              </div>
            )}
            {userData?.traderType && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Briefcase className="w-4 h-4 text-blue-500" />
                <span className="capitalize">{userData.traderType.replace('-', ' ')}</span>
              </div>
            )}
            {userData?.tradingExperience && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Award className="w-4 h-4 text-amber-500" />
                <span className="capitalize">{userData.tradingExperience}</span>
              </div>
            )}
          </div>

          {/* Right column - Follow Stats */}
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-1 text-gray-600">
              <Users className="w-4 h-4" />
              <span className="text-sm">
                <span className="font-bold text-gray-900">{followCounts.followers}</span> Followers
              </span>
            </div>
            <div className="flex items-center gap-1 text-gray-600">
              <span className="text-sm">
                <span className="font-bold text-gray-900">{followCounts.following}</span> Following
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white px-6 pb-6 flex gap-3 justify-center">
          <FollowButton
            targetUserId={userId}
            currentUserId={currentUserId}
            targetUserName={userData?.displayName}
          />
          <FriendRequestButton
            targetUserId={userId}
            currentUserId={currentUserId}
            targetUserName={userData?.displayName}
          />
        </div>
      </div>
    </div>
  );
}
